// El chat de un contratado como VENTANA sobre la oficina (S10): la oficina se ve atenuada detrás y la
// lista del equipo sigue a la izquierda para cambiar de agente. Cabecera como Telegram (avatar, nombre,
// línea de estado), la conversación, lo que te toca (FranjaAccion), el compositor y, a la derecha, la
// Información (se abre y se cierra con ⓘ o Ctrl/⌘+I). Enlazado a la URL (?agente=<id>); Esc cierra; Alt+↑/↓ cambia de agente.
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronLeft, DoorOpen, Info, Moon, MoreVertical, Pause, Pencil, Search, Sun, X, type LucideIcon } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../stores/app.store';
import { stopAgentRequest } from '../../../lib/api';
import { useOficinaStore } from '../store';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { Boton, BotonIcono, IconoEncargo } from '../ui';
import { lineaDeEstado, quien } from '../marco/estado';
import { ahoraMismo, Informacion } from '../expediente/Informacion';
import { DialogoCerrar } from '../cierre/DialogoCerrar';
import { ConversacionOficina } from './ConversacionOficina';
import { Buscador } from './Buscador';
import { Compositor } from './Compositor';
import { useTeclasChat } from './useTeclasChat';
import { FranjaAccion } from './FranjaAccion';
import { useMarcarLeido } from '../leidos';
import { useDeslizarAtras } from './deslizarAtras';
import { PedidoEnElChat } from '../pelotas/PedidoEnElChat';
import { useSubidas } from '../despliegues/store';
import { RevisarLaSubida } from '../despliegues/RevisarLaSubida';
import { aparcadoDe, etiquetaSituacion } from '../marco/situacion';
import { usePasoEnVivo } from './paso-en-vivo';
import { useSantiPorAgente } from '../pelotas/porAgente';

// 13-10 — clave nueva: la ficha arranca abierta para todos (Pau: «me gusta la derecha: está siempre ahí»); lo que
// quedó guardado de la información vieja (que casi nadie abría) no cuenta. 04-11: es la preferencia del panel FIJO al
// lado (una por aparato, no por agente, como Mensajes y Mail); el flotante arranca siempre cerrado.
const CLAVE_INFO = 'oficina:ficha-abierta';
/** 04-11 (ficha.md §3) — el panel va fijo al lado solo si a la conversación le quedan al menos 640 px; si no, flota. */
const ANCHO_PANEL = 360;
const CHAT_MINIMO = 640;

/** El ancho de un elemento, vivo (null mientras no se sabe, o sin ResizeObserver: entonces se supone que cabe al lado). */
function useAncho(ref: RefObject<HTMLElement | null>): number | null {
  const [ancho, setAncho] = useState<number | null>(null);
  // Antes de pintar: en un iPad la ficha no llega a asomar fija para luego saltar a flotante.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => setAncho(el.getBoundingClientRect().width || null);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return ancho;
}

/** «Ctrl I» o, en un Mac/iPad, «⌘I»: lo que dice el globo del botón ⓘ. */
function atajoInfo(): string {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘I' : 'Ctrl I';
}

/**
 * `movil` (la disposición de OficinaPage por debajo de 768 px): el chat ocupa la pantalla entera, «‹» a la
 * izquierda en vez de la ✕ y deslizar desde el borde izquierdo vuelve a la lista (09-10, maqueta aprobada).
 */
export function VentanaChat({ agentId, alCambiar, alCerrar, movil = false }: { agentId: string; alCambiar: (id: string) => void; alCerrar: () => void; movil?: boolean }) {
  const contratados = useOficinaStore((s) => s.contratados);
  const salas = useOficinaStore((s) => s.salas);
  const abrirChat = useOficinaStore((s) => s.abrirChat);
  const c = contratados[agentId];
  useMarcarLeido(c);
  // Tres maneras de estar la ficha (04-11, ficha.md §3):
  // - fija al lado (a la conversación le quedan ≥ 640 px): arranca abierta y se recuerda lo último que eligió Pau;
  // - flotante encima de la conversación (iPad, ventana estrecha): arranca cerrada y se cierra al pulsar fuera, con Esc o
  //   al cambiar de agente;
  // - en el móvil (10-10, punto 11) tapa el chat entero: arranca cerrada y no se recuerda.
  const ventana = useRef<HTMLElement>(null);
  const ancho = useAncho(ventana);
  const lateral = !movil && (ancho === null || ancho - ANCHO_PANEL >= CHAT_MINIMO);
  const [infoFija, setInfoFija] = useState(() => localStorage.getItem(CLAVE_INFO) !== '0');
  const [infoSuelta, setInfoSuelta] = useState(false);
  const info = movil || !lateral ? infoSuelta : infoFija;
  const setInfo = (abierta: boolean) => (movil || !lateral ? setInfoSuelta(abierta) : setInfoFija(abierta));
  const flotante = !movil && !lateral && info;
  useEffect(() => {
    setInfoSuelta(false);
  }, [agentId]);
  // 13-10 — el buscador del chat (lupa de la cabecera); se cierra al cambiar de agente.
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    setBuscando(false);
  }, [agentId]);
  // 04-11 — «Renombrar» desde el menú de la mesa (o F2 sobre ella) abre el chat con el nombre listo: la ficha se abre
  // aquí y su campo lo recoge (expediente/Informacion.tsx).
  const renombrar = useOficinaStore((s) => s.renombrarPedido === agentId);
  useEffect(() => {
    if (renombrar) setInfo(true);
    // Solo cuando llega el pedido: `setInfo` cambia con el ancho de la ventana.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renombrar]);
  // Esc y Alt+↑/↓ (en el orden que se ve en la lista, con «Con Santi») y Ctrl/⌘+I: useTeclasChat. Con la ficha
  // flotando encima, Esc la cierra a ella primero (como cualquier hoja: lo de más arriba se va antes).
  useTeclasChat({ agentId, contratados, alCambiar, alCerrar: flotante ? () => setInfoSuelta(false) : alCerrar, alAlternarInfo: () => setInfo(!info) });

  // El store solo recarga (y guarda) el expediente del chat en pantalla; al salir se olvida.
  useEffect(() => {
    abrirChat(agentId);
    return () => abrirChat(null);
  }, [abrirChat, agentId]);

  useEffect(() => {
    localStorage.setItem(CLAVE_INFO, infoFija ? '1' : '0');
  }, [infoFija]);

  // Con la ficha abierta en el móvil, deslizar desde el borde vuelve a la conversación (como su «‹»), no a la lista.
  useDeslizarAtras(movil, info && movil ? () => setInfo(false) : alCerrar);

  const sala = salas.find((s) => s.projectId === c?.projectId)?.nombre ?? t('oficinaV2.desconocido');

  return (
    // El hueco y el fondo atenuado los pone el marco (Escenario): aquí solo la ventana, que lo llena.
    <section
        ref={ventana}
        role="region"
        aria-label={c ? t('oficinaV2.chat.conversacionCon', { nombre: quien(c) }) : t('oficinaV2.chat.conversacion')}
        className={cn(
          'relative flex h-full w-full overflow-hidden bg-oficina-elevada animate-in fade-in-0 duration-300 ease-o-apple',
          movil ? '' : 'rounded-o-xl shadow-o-hoja zoom-in-[0.98]',
        )}
      >
        {c ? (
          <>
            {/* Con la ficha flotando encima, pulsar en la conversación la cierra (no en ⓘ ni en el nombre: esos la alternan). */}
            <div
              className="flex min-w-0 flex-1 flex-col"
              onPointerDownCapture={flotante ? (e) => !(e.target as HTMLElement).closest('[data-abre-info]') && setInfoSuelta(false) : undefined}
            >
              <Cabecera
                contratado={c}
                info={info}
                alInfo={() => setInfo(!info)}
                alRenombrar={() => {
                  setInfo(true);
                  useOficinaStore.getState().pedirRenombrar(c.agentId);
                }}
                alCerrar={alCerrar}
                movil={movil}
                alBuscar={() => setBuscando(true)}
              />
              <Cuerpo
                contratado={c}
                alCambiar={alCambiar}
                movil={movil}
                alAbrirFicha={movil ? () => setInfo(true) : undefined}
                buscando={buscando}
                alCerrarBusqueda={() => setBuscando(false)}
              />
            </div>
            {info ? <PanelInformacion contratado={c} sala={sala} movil={movil} flotante={!lateral} alVolver={() => setInfo(false)} /> : null}
          </>
        ) : (
          <div className="m-auto flex flex-col items-center gap-3 p-8 text-center">
            <p className="text-oficina-texto2">{t('oficinaV2.chat.sinAgente')}</p>
            <Boton onClick={alCerrar}>{t('oficinaV2.chat.volver')}</Boton>
          </div>
        )}
      </section>
  );
}

/**
 * Dónde va la Información. Escritorio: panel de 360 px a la derecha, fijo al lado si a la conversación le quedan ≥ 640 px;
 * si no (`flotante`), encima de la conversación por debajo de su cabecera, sin oscurecer ni desenfocar (04-11, ficha.md
 * §3). Móvil (10-10, punto 11 del informe de espacio): la pantalla entera con su «‹» — el panel de 360 px en 390 dejaba
 * una franja del chat a la izquierda que parecía un fallo.
 */
export function PanelInformacion({ contratado: c, sala, movil, flotante = false, alVolver }: { contratado: ContratadoDTO; sala: string; movil: boolean; flotante?: boolean; alVolver: () => void }) {
  if (movil) {
    return (
      <div data-testid="panel-informacion" className="absolute inset-0 z-30 flex flex-col bg-oficina-elevada animate-in fade-in-0">
        <header className="flex h-16 shrink-0 items-center gap-1.5 border-b border-oficina-linea pl-1 pr-2">
          <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.info.volverAlChat')} onClick={alVolver} />
          <h2 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.info.titulo')}</h2>
        </header>
        <div className="min-h-0 flex-1">
          <Informacion contratado={c} sala={sala} />
        </div>
      </div>
    );
  }
  return (
    <div
      id="panel-informacion"
      data-testid="panel-informacion"
      data-modo={flotante ? 'flotante' : 'lateral'}
      className={cn(
        // Entra deslizando 16 px con su opacidad (200 ms, curva de Apple); con «reducir movimiento», solo la opacidad.
        'w-[min(360px,100%)] overflow-hidden bg-oficina-panel animate-in fade-in-0 slide-in-from-right-4 duration-200 ease-o-apple motion-reduce:slide-in-from-right-0',
        flotante ? 'absolute bottom-0 right-0 top-16 z-20 rounded-bl-o-xl shadow-o-hoja' : 'relative shrink-0 border-l border-oficina-linea',
      )}
    >
      <Informacion contratado={c} sala={sala} />
    </div>
  );
}

export function Cabecera({ contratado: c, info, alInfo, alRenombrar, alCerrar, movil, alBuscar }: {
  contratado: ContratadoDTO; info: boolean; alInfo: () => void; alRenombrar?: () => void; alCerrar: () => void; movil: boolean; alBuscar?: () => void;
}) {
  // Tras «Cambiar el nombre» el foco es del campo de la ficha: el menú no debe devolverlo a ⋮ al cerrarse.
  const renombrando = useRef(false);
  const [cerrando, setCerrando] = useState(false);
  const cierrePedido = useOficinaStore((s) => s.cierrePedido === c.agentId);
  useEffect(() => {
    if (!cierrePedido) return;
    useOficinaStore.getState().pedirCierre(null);
    setCerrando(true);
  }, [cierrePedido]);
  const [parando, setParando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expediente = useOficinaStore((s) => s.expedientes[c.agentId]);
  const escribiendo = useAppStore((s) => s.agents[c.agentId]?.status === 'running');
  // Entrega 4 (flujo.md #4) — «te necesita» se dice UNA vez, en la línea de encima de la caja (LineaAhora, con su botón
  // cuando lo hay). Aquí solo el estado corto, el mismo que la lista («Pide tu permiso», «No puede seguir sin ti»), sin el
  // color de actuar: antes la cabecera repetía en oro «Te necesita: tiene una pregunta para ti».
  const linea = lineaDeEstado(c);
  const estado = linea.tono === 'acento' ? { texto: etiquetaSituacion(c.situacion), tono: 'normal' as const } : linea;
  // La misma cuenta que la lista (con Santi y su subida): si la lista lo pone en «Aparcados», aquí se ofrece despertarlo.
  const santi = useSantiPorAgente().get(c.agentId);
  const subida = useSubidas().porAgente.get(c.agentId);
  const dormido = !!aparcadoDe(c, santi, { subida: subida ?? null });
  const quieto = !['trabajando', 'llegando', 'cerrando', 'saliendo'].includes(c.situacion);
  return (
    <header className={cn('flex h-16 shrink-0 items-center gap-3 border-b border-oficina-linea bg-oficina-elevada pr-2', movil ? 'gap-1.5 pl-1' : 'pl-4')}>
      {/* 09-10 (maqueta aprobada) — en el móvil «‹» a la izquierda vuelve a la lista (como la ✕, que se esconde). */}
      {movil ? <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.chat.atras')} onClick={alCerrar} /> : null}
      <button type="button" data-abre-info onClick={alInfo} title={c.titulo && c.titulo !== c.tema ? c.titulo : undefined} className="flex min-w-0 flex-1 items-center gap-3 rounded-o-s text-left outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
        <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={42} />
        <span className="min-w-0">
          <span className="block truncate text-o-cuerpo font-semibold text-oficina-texto">
            {quien(c)}
          </span>
          <span
            className={cn(
              'block truncate text-o-sub',
              error ? 'text-oficina-error' : escribiendo && estado.tono === 'normal' ? 'text-oficina-enlace' : { acento: 'text-oficina-te-necesita', normal: 'text-oficina-texto2', aviso: 'text-oficina-bloqueado', error: 'text-oficina-error' }[estado.tono],
            )}
          >
            {error ?? (escribiendo && estado.tono === 'normal' ? t('oficinaV2.chat.escribiendo') : estado.texto)}
          </span>
        </span>
      </button>
      {alBuscar ? <BotonIcono icono={Search} etiqueta={t('oficinaV2.buscar.abrir')} onClick={alBuscar} /> : null}
      {/* 04-11 (ficha.md §3) — ⓘ entre la lupa y ⋮, con su estado pulsado y su atajo. En el móvil la abre la línea de
          encima de la caja (como hasta ahora). */}
      {movil ? null : (
        <BotonIcono
          icono={Info}
          etiqueta={t('oficinaV2.info.boton', { atajo: atajoInfo() })}
          aria-label={t('oficinaV2.info.titulo')}
          aria-expanded={info}
          aria-controls="panel-informacion"
          aria-keyshortcuts="Control+I Meta+I"
          data-abre-info
          className={info ? 'bg-oficina-control text-oficina-texto' : undefined}
          onClick={alInfo}
        />
      )}
      {escribiendo ? (
        <BotonIcono
          icono={Pause}
          etiqueta={parando ? t('oficinaV2.chat.interrumpiendo') : t('oficinaV2.chat.interrumpir')}
          disabled={parando}
          onClick={() => {
            setParando(true);
            setError(null);
            stopAgentRequest(c.agentId)
              .catch((e: unknown) => setError(mensajeError(e)))
              .finally(() => setParando(false));
          }}
        />
      ) : null}
      {/* 09-10 (maqueta aprobada «cabecera más limpia»): fijar e información van al menú ⋮, que está siempre.
          Crítica A9: «Terminar este trabajo» (irreversible) también ahí, lejos de la ✕ de cerrar la ventana. */}
      <Menu.Root>
        <Menu.Trigger asChild>
          <BotonIcono icono={MoreVertical} etiqueta={t('oficinaV2.chat.masAcciones')} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            align="end"
            sideOffset={6}
            onCloseAutoFocus={(e) => {
              // En el móvil, devolver el foco al ⋮ enciende su pista «Más acciones» encima de lo que se acaba de abrir
              // (p. ej. la ficha a pantalla entera); con el dedo no hace falta devolverlo.
              if (renombrando.current || movil) e.preventDefault();
              renombrando.current = false;
            }}
            className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
            {/* 04-11 (Entrega 3) — «Fijar arriba» se fue con el grupo «Fijados» (1 uso en 30 días); en su sitio, aparcar o
                despertar (Pau aprobó «Aparcar»). Lo que trabaja o se va no se aparca. */}
            {dormido ? (
              <ItemMenu icono={Sun} onSelect={() => void useOficinaStore.getState().aparcar(c.agentId, false)}>
                {t('oficinaV2.lista.aparcado.despertar')}
              </ItemMenu>
            ) : quieto ? (
              <ItemMenu icono={Moon} onSelect={() => void useOficinaStore.getState().aparcar(c.agentId, true)}>
                {t('oficinaV2.lista.aparcado.aparcar')}
              </ItemMenu>
            ) : null}
            <ItemMenu icono={Info} onSelect={alInfo}>
              {t(info ? 'oficinaV2.info.ocultar' : 'oficinaV2.info.ver')}
            </ItemMenu>
            {/* 10-10 — el nombre lo pone la oficina y solo lo cambia una persona: aquí, donde Pau busca «qué hago con este». */}
            {alRenombrar ? (
              <ItemMenu
                icono={Pencil}
                onSelect={() => {
                  renombrando.current = true;
                  alRenombrar();
                }}
              >
                {t('oficinaV2.info.renombrar')}
              </ItemMenu>
            ) : null}
            {c.ciclo === 'activo' ? (
              <>
                <Menu.Separator className="mx-2 my-1 h-px bg-white/10" />
                <ItemMenu icono={DoorOpen} onSelect={() => setCerrando(true)}>
                  {t('oficinaV2.cierre.boton')}
                </ItemMenu>
              </>
            ) : null}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
      {movil ? null : (
        <>
          <span className="mx-1 h-6 w-px bg-white/10" aria-hidden />
          <BotonIcono icono={X} etiqueta={t('oficinaV2.chat.cerrar')} onClick={alCerrar} />
        </>
      )}
      <DialogoCerrar contratado={c} expediente={expediente} abierto={cerrando} alCerrar={() => setCerrando(false)} />
    </header>
  );
}

function ItemMenu({ icono: Icono, onSelect, children }: { icono: LucideIcon; onSelect: () => void; children: React.ReactNode }) {
  return (
    <Menu.Item
      onSelect={onSelect}
      className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
    >
      <Icono className="h-4 w-4 text-oficina-texto2" aria-hidden />
      {children}
    </Menu.Item>
  );
}

/**
 * La línea fija de «qué pasa ahora» encima de lo que te toca y del compositor (13-10). En el móvil (Pau: «con el móvil,
 * si está arriba me molesta») es además el botón que abre la ficha: no hay nada encima del chat.
 */
export function LineaAhora({ contratado: c, alAbrirFicha }: { contratado: ContratadoDTO; alAbrirFicha?: () => void }) {
  const ficha = useOficinaStore((s) => s.expedientes[c.agentId]?.ficha ?? null);
  // 04-11 (Entrega 3) — si lo que pide es una subida, la línea lleva su botón: «Revisar la subida».
  const subida = useSubidas().porAgente.get(c.agentId);
  const a = ahoraMismo(c, ficha);
  // Entrega 4 (flujo.md #7) — mientras trabaja, el paso en vivo: «Paso 7 · Comprobando los precios · hace 20 s» (la misma
  // fuente que su fila de la lista, chat/paso-en-vivo.ts). Sin número si el turno empezó antes de abrir la página.
  const vivo = usePasoEnVivo(c.agentId, c.situacion === 'trabajando');
  if (subida) return <RevisarLaSubida d={subida} />;
  const enVivo = a.tono === 'trabajando' && vivo ? vivo : null;
  const etiqueta = enVivo ? (enVivo.paso ?? a.etiqueta) : a.etiqueta;
  const texto = enVivo ? enVivo.texto : a.texto;
  const Contenedor = alAbrirFicha ? 'button' : 'div';
  return (
    <Contenedor
      {...(alAbrirFicha ? { type: 'button' as const, onClick: alAbrirFicha, 'aria-label': `${etiqueta}${texto ? ` · ${texto}` : ''} — ${t('oficinaV2.info.ver')}` } : { 'aria-live': 'polite' as const })}
      data-testid="linea-ahora"
      data-tono={a.tono}
      data-vivo={enVivo ? '' : undefined}
      className={cn(
        // 03-11 (Apple): «ahora mismo» como una cápsula tranquila encima del compositor, no una franja de borde a borde.
        'mx-3 mt-1 flex min-h-10 shrink-0 items-center gap-2 rounded-o-m px-3.5 py-2 text-left text-o-texto sm:mx-5',
        a.tono === 'pide' ? 'bg-oficina-te-necesita/10 text-oficina-te-necesita-claro' : a.tono === 'problema' ? 'bg-oficina-situacion-problema-pau/10 text-oficina-error-claro' : 'bg-oficina-control/70 text-oficina-texto2',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          a.tono === 'trabajando' ? 'animate-pulse bg-oficina-trabajando' : a.tono === 'pide' ? 'bg-oficina-te-necesita' : a.tono === 'problema' ? 'bg-oficina-error' : a.tono === 'oficina' ? 'bg-oficina-cerrando' : 'bg-oficina-texto3',
        )}
      />
      <span className="min-w-0 truncate">
        <span className={cn('font-semibold', a.tono === 'trabajando' && 'text-oficina-texto', a.tono === 'parado' && 'text-oficina-texto')}>{etiqueta}</span>
        {texto ? <span>{` · ${texto}`}</span> : null}
      </span>
      {/* La hora no se corta nunca y no se lee en voz alta a cada segundo (la línea es aria-live). */}
      {enVivo ? (
        <span aria-hidden className={cn('-ml-2 shrink-0 whitespace-pre tabular-nums', enVivo.largo && 'text-oficina-bloqueado')} data-testid="paso-hace">
          {` · ${enVivo.hace}`}
        </span>
      ) : null}
      {alAbrirFicha ? <Info className="ml-auto h-4 w-4 shrink-0 opacity-70" aria-hidden /> : null}
    </Contenedor>
  );
}

function Cuerpo({ contratado: c, movil, alAbrirFicha, buscando = false, alCerrarBusqueda }: {
  contratado: ContratadoDTO; alCambiar: (id: string) => void; movil: boolean; alAbrirFicha?: () => void; buscando?: boolean; alCerrarBusqueda?: () => void;
}) {
  const agentId = c.agentId;
  const agent = useAppStore((s) => s.agents[agentId]);
  const expediente = useOficinaStore((s) => s.expedientes[agentId]);
  // Mientras se comprueba, revisa, publica o se recoge su mesa, un mensaje podría mover su trabajo después
  // de revisado (revisiones R1, V3): el compositor se sustituye por una franja. Mismo criterio que el servidor.
  const sinMensajes = expediente?.cierre.bloqueaMensajes ?? false;
  const messages = useAppStore((s) => s.messagesByAgent[agentId] ?? null);
  const loadMessages = useAppStore((s) => s.loadMessages);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setError(null);
    loadMessages(agentId).catch((e: unknown) => vivo && setError(mensajeError(e)));
    return () => {
      vivo = false;
    };
  }, [agentId, loadMessages]);

  return (
    <>
      <div className="relative flex min-h-0 flex-1 flex-col">
        {error && messages === null ? (
          <p className="m-auto px-6 text-center text-o-texto text-oficina-error">{error}</p>
        ) : !agent || messages === null ? (
          <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.chat.cargando')}</p>
        ) : (
          // 04-11 (pelotas, entrega 2): sin tarjetas «Para Santi»; si este agente dejó preparado un pedido para Santi, una
          // línea al final, «Te he preparado un pedido para Santi · Revisar», que abre «Pedir algo» con él.
          <ConversacionOficina contratado={c} mensajes={messages} traducciones={expediente?.ficha?.traducciones} resumenes={expediente?.ficha?.resumenes} pie={<PedidoEnElChat agentId={c.agentId} />} />
        )}
        {buscando && alCerrarBusqueda ? <Buscador contratado={c} alCerrar={alCerrarBusqueda} /> : null}
      </div>
      <LineaAhora contratado={c} alAbrirFicha={alAbrirFicha} />
      {/* 29-10 (Pau): fuera la barra «Siguiente: … Ir»; cada chat es solo la conversación. */}
      <FranjaAccion contratado={c} expediente={expediente} />
      {sinMensajes ? (
        <p
          className="shrink-0 border-t border-oficina-linea bg-oficina-elevada px-4 py-4 text-center text-o-texto text-oficina-texto2"
          style={movil ? { paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' } : undefined}
        >{t('oficinaV2.chat.cerrandoSinMensajes')}</p>
      ) : (
        <Compositor key={agentId} agentId={agentId} movil={movil} />
      )}
    </>
  );
}

